import filters from "@/components/filters/Filters";
import { SupervisionTimeCalculation } from "@/models/supervisionTimeCalculations";
import { weekAverageInHoursMinutesFormat } from "@/utils/numberUtils";
import { endOfDay, startOfDay, sub } from "date-fns";
import { Employment } from "../models/employments";
import { PositionCategory } from "../models/positionCategories";
import { Position } from "../models/positions";
import store from "../store";
import { extendArray } from "./types";

@extendArray
export class EmploymentArray extends Array<Employment> {
  constructor(employment: Employment[] | number) {
    if (employment instanceof Array) {
      super(...employment);
    } else {
      super(employment);
    }
    Object.setPrototypeOf(this, Object.create(EmploymentArray.prototype));
  }

  getEmploymentOnDate(date: Date, employeeid: number | undefined): Employment | undefined {
    const employmentOnStartDate = this.find(
      (e: Employment) =>
        (!employeeid || e.employee_id === employeeid) &&
        startOfDay(date) >= startOfDay(e.start_date) &&
        date <= endOfDay(e.end_date)
    );
    return employmentOnStartDate;
  }
}

export class EmploymentHandler {
  static async isOK21(positionId: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const fetchPositions = store.dispatch("positions/fetchPositions");
      const fetchPositionCategories = store.dispatch("positions/fetchPositionCategories");
      Promise.all([fetchPositions, fetchPositionCategories])
        .then(([positions, positionCategories]) => {
          const position: Position | undefined = positions.find((p) => p.id === positionId);
          const positionCategory: PositionCategory | undefined = positionCategories.find(
            (pc) => pc.id === position?.position_category_id
          );
          resolve(positionCategory ? positionCategory.isOK21() : false);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  static async substractSenorityFromNetTime(employment: Employment) {
    const isOK21 = await this.isOK21(employment.position_id);
    if (!isOK21) {
      return false;
    } else if (
      (isOK21 && startOfDay(employment.start_date) < startOfDay(this.senorityChangeDate)) ||
      employment.soCalculatesAsNotSet
    ) {
      // Besk. der starter før 1/8-2023 skal håndteres på gammel måde (planlagt arbejdstid), også selvom beskæftigelse slutter efter 1/8-2023
      return true;
    } else {
      return false;
    }
  }

  static get senorityChangeDate() {
    return startOfDay(new Date(2023, 7, 1)); // 1/8-2023
  }

  static async calculateAverageWorkingHours(
    planned: SupervisionTimeCalculation,
    employment: Employment
  ): Promise<{
    averageHoursPerDay: string;
    averageHoursPerWeek: string;
    workHours: number;
    workTimeForPlanning: number;
    workDaysForPlanning: number;
  }> {
    const substractSenority = await this.substractSenorityFromNetTime(employment);
    const correction = employment.correction ? employment.correction / 60 : 0;
    let workHours = planned?.supervision_hours ?? 0;
    let workDaysForPlanning = 0;
    if (planned) {
      workDaysForPlanning = (planned.mon_fri_days ?? 0) - (planned.public_holidays ?? 0);
      if (substractSenority) {
        workDaysForPlanning -= planned.seniority_days ?? 0;
      }
    }

    let averageWorkHoursPerDay = 0;

    let workTimeForPlanning = 0;

    if (await employment.isOK21()) {
      workHours -= correction;
      workTimeForPlanning =
        workHours -
        (planned.vacation_hours ? planned.vacation_hours : 0) -
        (planned.sixth_week_hours ? planned.sixth_week_hours : 0);

      if (employment.soCalculatesAsNormDay) {
        workDaysForPlanning -= planned.seniority_days ?? 0;
        workTimeForPlanning -= planned.seniority_hours ?? 0;
      }
      workDaysForPlanning =
        workDaysForPlanning - (planned.vacation_days ?? 0) - (planned.sixth_week_days ?? 0);

      averageWorkHoursPerDay = workTimeForPlanning / workDaysForPlanning;
    } else {
      averageWorkHoursPerDay = workHours / workDaysForPlanning;
    }
    return {
      averageHoursPerDay: filters.hourDecimalToHourColonMinutes(averageWorkHoursPerDay),
      averageHoursPerWeek: weekAverageInHoursMinutesFormat(averageWorkHoursPerDay),
      workHours,
      workTimeForPlanning,
      workDaysForPlanning
    };
  }
  
  static getFormattedRateOfEmployment(value: number): string {
    if (value) {
      return (value / 37).toFixed(4).replace(".", ",");
    } else {
      return "0";
    }
  }
}
